/**
 * WP-V2-WC — strefy przejść przy krawędziach świata.
 * Sceny podają EdgeZone[]; kontroler emituje zdarzenie przy wejściu w strefę.
 */
import { makeCardinalEdgeZones, pickEdgeZone } from './edgeLogic.js';
import type { EdgeZone } from './types.js';

export type EdgeTransitionOpts = {
  zones?: readonly EdgeZone[];
  /** Auto-strefy N/E/S/W gdy zones pominięte. */
  worldW?: number;
  worldH?: number;
  band?: number;
  /**
   * Callback przy wejściu w nową strefę (raz na wejście — hysteresis).
   * Scena decyduje: go(targetKey), dymek „dalej →" itd.
   */
  onEnter?: (zone: EdgeZone) => void;
  onLeave?: (zone: EdgeZone) => void;
};

export class EdgeTransition {
  private zones: EdgeZone[];
  private activeId: string | null = null;
  private onEnter?: (zone: EdgeZone) => void;
  private onLeave?: (zone: EdgeZone) => void;

  constructor(opts: EdgeTransitionOpts = {}) {
    if (opts.zones) {
      this.zones = [...opts.zones];
    } else if (opts.worldW != null && opts.worldH != null) {
      this.zones = makeCardinalEdgeZones(opts.worldW, opts.worldH, opts.band ?? 48);
    } else {
      this.zones = [];
    }
    this.onEnter = opts.onEnter;
    this.onLeave = opts.onLeave;
  }

  setZones(zones: readonly EdgeZone[]): void {
    this.zones = [...zones];
    this.activeId = null;
  }

  getZones(): readonly EdgeZone[] {
    return this.zones;
  }

  /**
   * Sprawdź pozycję gracza. Zwraca aktywną strefę (lub null).
   * Wywołuje onEnter/onLeave przy zmianie.
   */
  update(x: number, y: number): EdgeZone | null {
    const zone = pickEdgeZone(x, y, this.zones);
    const nextId = zone?.id ?? null;

    if (nextId !== this.activeId) {
      if (this.activeId) {
        const prev = this.zones.find((z) => z.id === this.activeId);
        if (prev) this.onLeave?.(prev);
      }
      if (zone) this.onEnter?.(zone);
      this.activeId = nextId;
    }

    return zone;
  }

  /** Aktualna strefa bez side-effectów. */
  peek(x: number, y: number): EdgeZone | null {
    return pickEdgeZone(x, y, this.zones);
  }

  get activeZoneId(): string | null {
    return this.activeId;
  }

  /**
   * Opcjonalny podgląd debug (półprzezroczyste pasy) — tylko dev.
   * Typ scene: duck-typing (unika twardego importu Phaser w testach).
   */
  debugDraw(
    scene: {
      add: {
        rectangle: (
          x: number,
          y: number,
          w: number,
          h: number,
          color: number,
          alpha: number,
        ) => { setDepth: (d: number) => unknown };
      };
    },
    depth = 50,
  ): unknown[] {
    return this.zones.map((z) =>
      scene.add
        .rectangle(
          z.rect.x + z.rect.width / 2,
          z.rect.y + z.rect.height / 2,
          z.rect.width,
          z.rect.height,
          0x90ee90,
          0.18,
        )
        .setDepth(depth),
    );
  }

  destroy(): void {
    this.zones = [];
    this.activeId = null;
  }
}

// Re-export pure helpers for consumers / tests
export { pickEdgeZone, makeCardinalEdgeZones, pointInRect } from './edgeLogic.js';
